import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { wakeRafBriefly } from "../../utils/wakeRaf";
import Page from "../molecules/Page";
import api from "../../api/apiHelper";
import { GoogleLogin } from '@react-oauth/google';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from "@capgo/capacitor-social-login";
import * as Sentry from '@sentry/react';
import { handleAuthError, reportMissingToken } from '../../utils/authErrorHelper';
import { invalidateApiCache } from '../../utils/apiCache';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import { trackRegistration } from '../../api/tiktokEvents';
import googleIcon from '../../img/icons/google.svg';
import appleIcon from '../../img/icons/apple.svg'; 
import posthog from 'posthog-js';

axios.defaults.withCredentials = true;

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const csrftoken = getCookie('csrftoken');
if (csrftoken) {
  axios.defaults.headers.common['X-CSRFToken'] = csrftoken;
}

const apiUrl = process.env.REACT_APP_API_SOURCE;

const REGISTER_STEPS = ['Tworzenie konta', 'Budowanie profilu', 'Jeszcze chwilkę', 'Logowanie'];

const Register = () => {
  function getCookieInternal(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for(let i=0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  const [nickname, setNickname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(null); // null | 'email' | 'google' | 'apple'
  const [regStepIndex, setRegStepIndex] = useState(0);

  // Rejestracja jest długa (konto + profil + zgody + auto-login) — przewijaj etapy na przycisku,
  // żeby user wiedział że coś się dzieje. Progresja + clamp na ostatnim (NIE zapętlamy).
  useEffect(() => {
    if (authLoading !== 'email') { setRegStepIndex(0); return; }
    const id = setInterval(() => {
      setRegStepIndex((prev) => Math.min(prev + 1, REGISTER_STEPS.length - 1));
    }, 2200);
    return () => clearInterval(id);
  }, [authLoading]);
  const [language, setLanguage] = useState("pl");
  const [fields, setFields] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState(["Matura Podstawowa"]);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [passwordHints, setPasswordHints] = useState([]);
  const [socialLoginFailed, setSocialLoginFailed] = useState(false);
  const [marketingSource, setMarketingSource] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const allConsentsAccepted = termsAccepted && marketingConsent && analyticsConsent;
  const handleSelectAllConsents = (checked) => {
    setTermsAccepted(checked);
    setMarketingConsent(checked);
    setAnalyticsConsent(checked);
  };
  const navigate = useNavigate();
  const [platform, setPlatform] = useState('web');
  const { consentState, handleSaveConsent } = useCookieConsent();

  useEffect(() => {
    posthog.capture('registration_started');
  }, []);

  useEffect(() => {
    const currentPlatform = Capacitor.getPlatform();
    setPlatform(currentPlatform);

    if (currentPlatform !== 'web') {
      const initializeCapacitorPlugin = async () => {
        try {
          if (currentPlatform === 'ios') {
            await SocialLogin.initialize({
              apple: {
                clientId: 'matematly',
              },
            });
          }
          if (currentPlatform === 'android') {
            await SocialLogin.initialize({
              google: {
                webClientId: process.env.REACT_APP_GOOGLE_CLIENT_ID
              },
            });
          }
          console.log(`Capacitor Social Login initialized on ${currentPlatform} for Register page.`);
        } catch (e) {
          Sentry.captureException(e, {
            tags: { auth_method: 'social_plugin', auth_step: 'plugin_init', platform: currentPlatform },
            extra: { page: 'Register', platform: currentPlatform },
          });
          console.error("Failed to initialize Capacitor Social Login on Register page", e);
        }
      };
      initializeCapacitorPlugin();
    }
  }, []);

  useEffect(() => {
    const fetchFields = async () => {
      try {
        const response = await axios.get(`${apiUrl}all_fields/`);
        setFields(response.data.all_fields);
        setAvailableFields(
          response.data.all_fields.filter((field) => field.lang === language)
        );
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'register', step: 'fetch_fields' },
          extra: { language, apiUrl: `${apiUrl}all_fields/` },
        });
        setError("Nie udało się pobrać dziedzin.");
      }
    };
    fetchFields();
  }, [language]);

  const validatePassword = (pass) => {
    const hints = [];
    if (pass.length < 8) hints.push("Hasło musi mieć co najmniej 8 znaków.");
    if (!/[A-Z]/.test(pass)) hints.push("Hasło musi zawierać co najmniej jedną wielką literę.");
    if (!/[a-z]/.test(pass)) hints.push("Hasło musi zawierać co najmniej jedną małą literę.");
    if (!/[0-9!@#$%^&*(),.?":{}|<>]/.test(pass)) hints.push("Hasło musi zawierać co najmniej jedną cyfrę lub znak specjalny.");
    setPasswordHints(hints);
    return hints.length ? hints.join(" ") : null;
  };

  const recordConsents = async () => {
    const consentsToRecord = [];
    if (termsAccepted) {
      consentsToRecord.push({ consent_type: 'TERMS_OF_SERVICE', status: 'GRANTED', version: '2.0' });
      consentsToRecord.push({ consent_type: 'PRIVACY_POLICY', status: 'GRANTED', version: '1.0' });
    }
    consentsToRecord.push({ consent_type: 'EMAIL_MARKETING', status: marketingConsent ? 'GRANTED' : 'REVOKED', version: '1.0' });
    consentsToRecord.push({ consent_type: 'ANALYTICS', status: analyticsConsent ? 'GRANTED' : 'REVOKED', version: '1.0' });

    // W tle — leci już po przejściu na pulpit, spinner mrugał nad onboardingiem.
    const promises = consentsToRecord.map(consent => api.post('/record-consent/', consent, { skipLoading: true }));
    await Promise.allSettled(promises);
  };

  const handleSocialLoginSuccess = (response, { isNewAccount = false } = {}) => {
    const { token, preferences } = response.data;
    
    localStorage.setItem("token", token);
    localStorage.setItem("user_id", preferences.user_id);
    localStorage.setItem("user_preferences", JSON.stringify(preferences));
    invalidateApiCache("user_dashboard_data", true);

    if (preferences && preferences.user_id) {
        posthog.identify(preferences.user_id);
        posthog.people.set({
            platform: Capacitor.getPlatform(),
            is_mobile_app: Capacitor.isNativePlatform(),
            user_id: preferences.user_id
        });

        // Rejestracja mailem konczy sie zalozeniem konta z is_profile_complete=true
        // (backend ustawia je w RegisterSerializer), wiec ta sciezka mowi wprost
        // `isNewAccount`. Dla Google/Apple konto powstaje z niedokonczonym profilem
        // i to jest nasz jedyny sygnal "rejestracja, nie logowanie".
        if (isNewAccount || preferences.is_profile_complete === false) {
            trackRegistration(preferences.user_id);
        }
    }
    navigate("/app");
    wakeRafBriefly();
  };

  const handleWebGoogleLoginSuccess = async (credentialResponse) => {
    setError(null);
    setSocialLoginFailed(false);
    setAuthLoading('google');
    try {
      const response = await axios.post(`${apiUrl}google-login/`, {
        token: credentialResponse.credential,
        platform: Capacitor.getPlatform(),
      });
      if (response.status === 200) {
        handleSocialLoginSuccess(response);
      }
    } catch (error) {
      setAuthLoading(null);
      const msg = handleAuthError(error, {
        method: 'google_web',
        step: 'backend_request',
        fallbackMessage: 'Logowanie przez Google nie powiodło się. Spróbuj ponownie.',
      });
      if (msg) {
        setError(msg);
        setSocialLoginFailed(true);
      }
    }
  };

  const handleNativeGoogleLogin = async () => {
    setError(null);
    setSocialLoginFailed(false);
    setAuthLoading('google');

    let result;
    try {
      result = await SocialLogin.login({ provider: 'google', options: { scopes: ['email', 'profile'] } });
    } catch (err) {
      setAuthLoading(null);
      const msg = handleAuthError(err, {
        method: 'google_native',
        step: 'plugin_login',
        fallbackMessage: 'Logowanie Google nie powiodło się. Spróbuj ponownie.',
      });
      if (msg) {
        setError(msg);
        setSocialLoginFailed(true);
      }
      return;
    }

    if (!result?.result?.idToken) {
      setAuthLoading(null);
      const isRealProblem = reportMissingToken('google_native', result);
      if (isRealProblem) {
        setError("Nie uzyskano danych z Google. Spróbuj ponownie.");
        setSocialLoginFailed(true);
      }
      return;
    }

    try {
      const response = await axios.post(`${apiUrl}google-login/`, {
        token: result.result.idToken,
        platform: Capacitor.getPlatform(),
      });
      if (response.status === 200) {
        handleSocialLoginSuccess(response);
      }
    } catch (err) {
      setAuthLoading(null);
      const msg = handleAuthError(err, {
        method: 'google_native',
        step: 'backend_request',
        fallbackMessage: 'Nie udało się dokończyć logowania Google. Spróbuj ponownie.',
      });
      if (msg) {
        setError(msg);
        setSocialLoginFailed(true);
      }
    }
  };

  const handleGoogleLoginError = () => {
    setAuthLoading(null);
    Sentry.captureMessage('Google Login widget error (Register)', {
      level: 'warning',
      tags: { auth_method: 'google_web', auth_step: 'widget_error', platform: 'web' },
    });
    setError("Wystąpił błąd podczas logowania przez Google.");
    setSocialLoginFailed(true);
  };

  const handleWebAppleSignIn = () => {
    setAuthLoading('apple');
    const client_id = 'matematly';
    const redirect_uri = process.env.REACT_APP_APPLE_REDIRECT_URI;
    const state = Math.random().toString(36).substring(2, 15);
    const appleAuthUrl = `https://appleid.apple.com/auth/authorize?client_id=${client_id}&redirect_uri=${encodeURIComponent(redirect_uri)}&response_type=code%20id_token&scope=name%20email&response_mode=form_post&state=${state}`;
    window.location.href = appleAuthUrl;
  };
  
  const handleNativeAppleLogin = async () => {
    setError(null);
    setSocialLoginFailed(false);
    setAuthLoading('apple');

    let result;
    try {
      result = await SocialLogin.login({ provider: 'apple', options: { scopes: ['email', 'name'] } });
    } catch (err) {
      setAuthLoading(null);
      const msg = handleAuthError(err, {
        method: 'apple_native',
        step: 'plugin_login',
        fallbackMessage: 'Logowanie Apple nie powiodło się. Spróbuj ponownie.',
      });
      if (msg) {
        setError(msg);
        setSocialLoginFailed(true);
      }
      return;
    }

    if (!result?.result?.idToken) {
      setAuthLoading(null);
      const isRealProblem = reportMissingToken('apple_native', result);
      if (isRealProblem) {
        setError("Nie uzyskano danych z Apple. Spróbuj ponownie.");
        setSocialLoginFailed(true);
      }
      return;
    }

    try {
      const response = await axios.post(`${apiUrl}mobile-apple-login/`, {
        token: result.result.idToken,
        platform: Capacitor.getPlatform(),
      });
      if (response.status === 200) {
        handleSocialLoginSuccess(response);
      }
    } catch (err) {
      setAuthLoading(null);
      const msg = handleAuthError(err, {
        method: 'apple_native',
        step: 'backend_request',
        fallbackMessage: 'Nie udało się dokończyć logowania Apple. Spróbuj ponownie.',
      });
      if (msg) {
        setError(msg);
        setSocialLoginFailed(true);
      }
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setSocialLoginFailed(false);

    if (availableFields.length === 0) {
      setError("Dane nie zostały jeszcze załadowane. Spróbuj ponownie za chwilę.");
      return;
    }
    if (password !== confirmPassword) {
      setFieldErrors((prev) => ({ ...prev, confirmPassword: "Hasła nie są zgodne." }));
      return;
    }
    const passwordError = validatePassword(password);
    if (passwordError) {
      setFieldErrors((prev) => ({ ...prev, password: passwordError }));
      return;
    }
    if (!termsAccepted) {
      setFieldErrors((prev) => ({ ...prev, terms: "Akceptacja regulaminu i polityki prywatności jest obowiązkowa." }));
      return;
    }

    const fieldsToSubmit = availableFields.filter((f) => selectedLevels.includes(f.knowledge_level)).map((f) => f.field_id);
    if (fieldsToSubmit.length === 0) {
      setError("Nie znaleziono przedmiotów dla poziomu podstawowego. Skontaktuj się z administratorem.");
      return;
    }

    const anonymousId = getCookieInternal('anonymousId');

    posthog.capture('registration_submitted', { method: 'email' });

    setAuthLoading('email');
    try {
      await axios.post(`${apiUrl}register_user/`, {
        nickname, email, password,
        platform: Capacitor.getPlatform(),
        lang: language,
        marketing_source: marketingSource,
        marketing_consent: marketingConsent,
        analytics_consent: analyticsConsent,
        selected_fields: fieldsToSubmit,
        anonymous_id: anonymousId,
      });
    } catch (err) {
      if (err.response && err.response.status === 400) {
        const respErr = err.response.data;
        const newFieldErrors = {};
        if (respErr.nickname) newFieldErrors.nickname = respErr.nickname.join(" ");
        if (respErr.email) newFieldErrors.email = respErr.email.join(" ");
        if (respErr.password) newFieldErrors.password = respErr.password.join(" ");
        if (respErr.selected_fields) newFieldErrors.selected_fields = respErr.selected_fields.join(" ");
        setFieldErrors(newFieldErrors);

        // Tylko KLUCZE pól, nigdy wartości (żeby nie wysłać wpisanego e-maila).
        const invalidFields = Object.keys(respErr);
        posthog.capture('registration_failed', { method: 'email', reason: 'validation_400', invalid_fields: invalidFields });
        Sentry.captureMessage('Błąd walidacji rejestracji', {
          level: 'info',
          tags: { auth_method: 'register', auth_step: 'validation_400' },
          extra: { invalid_fields: invalidFields },
        });
      } else {
        posthog.capture('registration_failed', { method: 'email', reason: 'server_error', status: err.response?.status || null });
        const msg = handleAuthError(err, {
          method: 'register',
          step: 'register',
          fallbackMessage: 'Rejestracja nie powiodła się. Spróbuj ponownie później.',
        });
        if (msg) {
          setError(msg);
        }
      }
      setAuthLoading(null);
      return;
    }

    posthog.capture('registration_succeeded', { method: 'email' });

    // Spinamy wybór z rejestracji z realnym stanem PostHoga (opt-in/opt-out + localStorage).
    // marketing (cookies) zostawiamy bez zmian — to inna zgoda niż EMAIL_MARKETING.
    handleSaveConsent({ ...consentState, analytics: analyticsConsent });

    try {
      const loginRes = await axios.post(`${apiUrl}login/`, { email, password });
      // Flaga przed nawigacją — pulpit czyta ją zaraz po załadowaniu, nie może czekać na zgody.
      localStorage.setItem('showOnboarding', 'true');
      handleSocialLoginSuccess(loginRes, { isNewAccount: true });
      await recordConsents();
    } catch (err) {
      posthog.capture('registration_failed', { method: 'email', reason: 'post_register_login', status: err.response?.status || null });
      const msg = handleAuthError(err, {
        method: 'register',
        step: 'post_register_login',
        fallbackMessage: 'Konto zostało utworzone, ale automatyczne logowanie nie powiodło się. Spróbuj się zalogować ręcznie.',
      });
      if (msg) {
        setError(msg);
      }
      setAuthLoading(null);
    }
  };

  return (
    <Page showNav={false}>
      <div className="bg-white lg:bg-transparent w-full">
      <div className="flex flex-col max-w-2xl mx-auto w-full px-4 lg:px-0">
        <div className="w-full lg:px-14 py-8 lg:py-10 lg:mt-[8vh] lg:bg-white lg:rounded-[3rem] lg:shadow-[4px_4px_12px_rgba(0,0,0,0.30)] lg:border lg:border-gray-200">
          <h2 className="text-center text-xl font-apotek uppercase tracking-widest font-semibold text-[#233855] mb-6">
            Zarejestruj się
          </h2>
          
          <div className="flex flex-col gap-3 w-full">
            {(platform === 'ios' || platform === 'web') && (
              <button
                type="button"
                onClick={platform === 'ios' ? handleNativeAppleLogin : handleWebAppleSignIn}
                disabled={!!authLoading}
                className="w-full px-4 py-2.5 border border-gray-300 flex justify-center items-center gap-2 rounded-lg text-gray-700 hover:border-gray-400 hover:text-gray-900 hover:shadow-sm transition-all duration-150 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
              >
                {authLoading === 'apple' ? (
                  <span className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                ) : (
                  <img className="w-5 h-5" src={appleIcon} loading="lazy" alt="apple logo" />
                )}
                <span>{authLoading === 'apple' ? "Łączenie…" : "Kontynuuj z Apple"}</span>
              </button>
            )}
            {platform !== 'web' ? (
              <button
                type="button"
                onClick={handleNativeGoogleLogin}
                disabled={!!authLoading}
                className="w-full px-4 py-2.5 border border-gray-300 flex justify-center items-center gap-2 rounded-lg text-gray-700 hover:border-gray-400 hover:text-gray-900 hover:shadow-sm transition-all duration-150 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
              >
                {authLoading === 'google' ? (
                  <span className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                ) : (
                  <img className="w-5 h-5" src={googleIcon} loading="lazy" alt="google logo" />
                )}
                <span>{authLoading === 'google' ? "Łączenie…" : "Kontynuuj z Google"}</span>
              </button>
            ) : (
              <div style={{ width: 'fit-content', margin: '0 auto' }} className={authLoading ? 'opacity-60 pointer-events-none' : ''}>
                <GoogleLogin
                  onSuccess={handleWebGoogleLoginSuccess}
                  onError={handleGoogleLoginError}
                  useOneTap
                  size="large"
                />
              </div>
            )}
          </div>

          {socialLoginFailed && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-center">
              <p className="text-sm text-amber-800">
                {error}
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Możesz też utworzyć konto za pomocą adresu email i hasła poniżej.
              </p>
            </div>
          )}
          
          <div className="relative flex py-5 items-center">
            <div className="flex-grow border-t border-gray-200"></div>
            <span className="flex-shrink mx-4 text-gray-400 text-xs uppercase tracking-widest">Lub</span>
            <div className="flex-grow border-t border-gray-200"></div>
          </div>
          
          <form className="space-y-5" onSubmit={handleRegister}>
            <div>
              <label htmlFor="nickname" className="block text-[0.8rem] font-apotek uppercase tracking-widest font-semibold text-[#233855] mb-2">
                Jak masz na imię? <span className="text-red-600 ml-1">*</span>
              </label>
              <input type="text" name="nickname" id="nickname" required value={nickname} onChange={(e) => setNickname(e.target.value)} className="ph-no-capture block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-base text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-[#233855] focus:ring-1 focus:ring-[#233855] sm:text-sm/6" />
              {fieldErrors.nickname && <p className="text-xs text-red-600 mt-1">{fieldErrors.nickname}</p>}
            </div>
            <div>
              <label htmlFor="email" className="block text-[0.8rem] font-apotek uppercase tracking-widest font-semibold text-[#233855] mb-2">
                Adres email <span className="text-red-600 ml-1">*</span>
              </label>
              <input type="email" name="email" id="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="ph-no-capture block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-base text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-[#233855] focus:ring-1 focus:ring-[#233855] sm:text-sm/6" />
              {fieldErrors.email && <p className="text-xs text-red-600 mt-1">{fieldErrors.email}</p>}
            </div>
            <div>
              <label htmlFor="password" className="block text-[0.8rem] font-apotek uppercase tracking-widest font-semibold text-[#233855] mb-2">
                Hasło <span className="text-red-600 ml-1">*</span>
              </label>
              <div className="space-y-2">
                <input type="password" name="password" id="password" placeholder="Wpisz hasło" autoComplete="new-password" required value={password} onChange={(e) => { setPassword(e.target.value); validatePassword(e.target.value); }} className="block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-base text-gray-900 placeholder:text-gray-400 focus:border-[#233855] focus:ring-1 focus:ring-[#233855] sm:text-sm/6" />
                <input type="password" name="password_confirm" id="password_confirm" placeholder="Powtórz hasło" autoComplete="new-password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-base text-gray-900 placeholder:text-gray-400 focus:border-[#233855] focus:ring-1 focus:ring-[#233855] sm:text-sm/6" />
              </div>
              {passwordHints.length > 0 && <ul className="mt-2 text-xs list-disc text-gray-600 ml-5">{passwordHints.map((hint, index) => (<li key={index}>{hint}</li>))}</ul>}
              {fieldErrors.password && <p className="text-xs text-red-600 mt-1">{fieldErrors.password}</p>}
              {fieldErrors.confirmPassword && <p className="text-xs text-red-600 mt-1">{fieldErrors.confirmPassword}</p>}
            </div>
            <div>
              <label htmlFor="marketing-source" className="block text-[0.8rem] font-apotek uppercase tracking-widest font-semibold text-[#233855] mb-2">
                Jak się o nas dowiedziałaś/eś? <span className="text-red-600 ml-1">*</span>
              </label>
              <select id="marketing-source" name="marketing-source" value={marketingSource} onChange={(e) => setMarketingSource(e.target.value)} required className="block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-base text-gray-900 placeholder:text-gray-400 focus:border-[#233855] focus:ring-1 focus:ring-[#233855] sm:text-sm/6">
                <option value="" disabled>Wybierz opcję...</option>
                <option value="Facebook">Facebook</option>
                <option value="Instagram">Instagram</option>
                <option value="TikTok">TikTok</option>
                <option value="Youtube">YouTube</option>
                <option value="Wyszukiwarka">Wyszukiwarka</option>
                <option value="Od znajomego">Od znajomego</option>
                <option value="Inne">Inne</option>
              </select>
              {fieldErrors.marketing_source && <p className="text-xs text-red-600 mt-1">{fieldErrors.marketing_source}</p>}
            </div>

            <div className="pt-2 space-y-4">
              <div className="relative flex items-center">
                <div className="flex h-5 items-center">
                  <input id="select-all-consents" name="select-all-consents" type="checkbox" checked={allConsentsAccepted} onChange={(e) => handleSelectAllConsents(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-[#233855] focus:ring-[#345081] cursor-pointer" />
                </div>
                <label htmlFor="select-all-consents" className="ml-3 text-sm font-bold text-[#233855] cursor-pointer select-none">
                  Zaznacz wszystkie zgody
                </label>
              </div>

              <div className="relative flex items-start">
                <div className="flex h-5 items-center mt-0.5">
                  <input id="terms" name="terms" type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-[#233855] focus:ring-[#345081] cursor-pointer" />
                </div>
                <div className="ml-3 text-sm text-gray-700 text-justify leading-snug">
                  <label htmlFor="terms" className="cursor-pointer">
                    Oświadczam, że zapoznałem się z{' '}
                    <a href="https://matematly.pl/terms-of-service" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#8A0000] hover:text-[#345081]">regulaminem aplikacji</a>
                    {' '}oraz{' '}
                    <a href="https://matematly.pl/privacy-policy" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#8A0000] hover:text-[#345081]">polityką prywatności</a>
                    {' '}oraz akceptuję regulamin
                    <span className="text-red-600 ml-1">*</span>
                  </label>
                  {fieldErrors.terms && <p className="text-xs text-red-600 mt-1">{fieldErrors.terms}</p>}
                </div>
              </div>

              <div className="relative flex items-start">
                <div className="flex h-5 items-center mt-0.5">
                  <input id="marketing" name="marketing" type="checkbox" checked={marketingConsent} onChange={(e) => setMarketingConsent(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-[#233855] focus:ring-[#345081] cursor-pointer" />
                </div>
                <div className="ml-3 text-[0.7rem] leading-relaxed text-gray-600 text-justify">
                  <label htmlFor="marketing" className="cursor-pointer">
                    Wyrażam zgodę na używanie mojego adresu e-mail przez <strong>MATEMATLY sp. z o.o.</strong> do celów przesyłania informacji handlowej w rozumieniu przepisów ustawy z dnia 18 lipca 2002 r. o świadczeniu usług drogą elektroniczną, w tym marketingu bezpośredniego, za pośrednictwem poczty elektronicznej.
                  </label>
                </div>
              </div>

              <div className="relative flex items-start">
                <div className="flex h-5 items-center mt-0.5">
                  <input id="analytics" name="analytics" type="checkbox" checked={analyticsConsent} onChange={(e) => setAnalyticsConsent(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-[#233855] focus:ring-[#345081] cursor-pointer" />
                </div>
                <div className="ml-3 text-[0.7rem] leading-relaxed text-gray-600 text-justify">
                  <label htmlFor="analytics" className="cursor-pointer">
                    Wyrażam zgodę na przetwarzanie przez <strong>MATEMATLY sp. z o.o.</strong> danych dotyczących mojego sposobu korzystania z aplikacji w celach analitycznych, w szczególności w celu ulepszania i rozwoju produktu. Zgodę mogę w każdej chwili wycofać w ustawieniach, co nie wpływa na zgodność z prawem przetwarzania dokonanego przed jej wycofaniem.
                  </label>
                </div>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <p className="text-[0.65rem] text-gray-500 leading-tight text-justify">
                      Administratorem danych osobowych zbieranych za pośrednictwem aplikacji jest <strong>MATEMATLY sp. z o.o. w Krakowie</strong>. Dane są lub mogą być przetwarzane w celach oraz na podstawach wskazanych szczegółowo w polityce prywatności (np. dokonywanie zakupów, marketing bezpośredni). Polityka prywatności zawiera pełną informację na temat przetwarzania danych przez administratora wraz z prawami przysługującymi osobie, której dane dotyczą. Szybki kontakt z administratorem: <a href="mailto:kontakt@matematly.pl" className="text-gray-700 hover:underline">kontakt@matematly.pl</a>.
                  </p>
              </div>
            </div>

            {error && !socialLoginFailed && <p className="text-xs text-center text-red-600 pt-2">{error}</p>}

            <div className="pt-4">
              <button type="submit" disabled={!!authLoading} className="flex w-full justify-center items-center gap-2 rounded-lg bg-[#233855] px-3 py-2.5 text-sm font-apotek uppercase tracking-widest text-white shadow-lg hover:bg-[#345081] active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100">
                {authLoading === 'email' && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
                {authLoading === 'email' ? `${REGISTER_STEPS[regStepIndex]}…` : "Stwórz konto"}
              </button>
            </div>
            <div className="flex w-full justify-center ">
              <div className="text-sm text-gray-600">
                Masz już konto?{" "}
                <a href="/login" className="font-semibold text-[#233855] hover:text-[#345081]">
                  Zaloguj się
                </a>
              </div>
            </div>
          </form>
        </div>
      </div>
      </div>
    </Page>
  );
};

export default Register;